import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SatelliteProductQuery {
  take?: number;
  offset?: number;
  sortBy?: string;
  sort?: number;
  filters?: Array<{
    filterField: string;
    filterValue: string;
    filterOperator: number;
  }>;
}

export interface SatelliteProduct {
  id?: string;
  productId?: string;
  name?: string;
  fileName?: string;
  mission?: string;
  instrument?: string;
  createAt: Date;
  originDate?: string;
  sensing_time?: string;
  sizeMb?: number;
  previewImagePath?: string;
  sentinelInspireMetadataId?: string;
  isPublic?: boolean;
  [key: string]: any;
}

export interface SatelliteProductResponse {
  data?: SatelliteProduct[];
  total?: number;
  [key: string]: any;
}

export interface CustomGetRequest {
  take?: number;
  offset?: number;
  sortBy?: string;
  sort?: number;
  regionId?: string;
  parentRegionId?: string; // ID родительского региона (level 2)
  satelliteType?: number; // 1 = Sentinel2, 2 = Landsat
  date?: string; // YYYY-MM-DD
  startDate?: string;
  endDate?: string;
  tagIds?: string[];
  north?: number;
  south?: number;
  east?: number;
  west?: number;
  maxCloudCover?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SatelliteProductsService {
  private baseUrl = environment.api;

  constructor(private http: HttpClient) {}

  get(apiUrl: string, params?: any): Observable<any> {
    const url = `${this.baseUrl}${apiUrl}`;
    return this.http.get(url, { params });
  }

  post(apiUrl: string, data: any): Observable<any> {
    const url = `${this.baseUrl}${apiUrl}`;
    return this.http.post(url, data);
  }

  put(apiUrl: string, data: any): Observable<any> {
    const url = `${this.baseUrl}${apiUrl}`;
    return this.http.put(url, data);
  }

  getById(apiUrl: string, id: string): Observable<any> {
    const url = `${this.baseUrl}${apiUrl}/${id}`;
    return this.http.get(url);
  }

  getFile(previewImagePath: string): Observable<Blob> {
    const url = `${this.baseUrl}/common/FileStorageItem/GetFile/${previewImagePath}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getFileWithProgress(previewImagePath: string): Observable<any> {
    const url = `${this.baseUrl}/common/FileStorageItem/GetFile/${previewImagePath}`;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'events',
      reportProgress: true
     });
   }

   // Specific methods for satellite products
   list(query: SatelliteProductQuery = { take: 10 }): Observable<SatelliteProductResponse> {
    return this.post('/common/SatelliteProduct/GetByQuery', query);
  }

  getTotalCount(query: SatelliteProductQuery): Observable<number> {
    return this.post('/common/SatelliteProduct/GetTotalCount', query);
  }

  getProductById(id: string): Observable<SatelliteProduct> {
    return this.getById('/common/SatelliteProduct/GetById', id);
  }

  // Sentinel2 Product Metadata
  getSentinel2ByQuery(query: any): Observable<any> {
    return this.post('/satellite/Sentinel2ProductMetadata/GetByQuery', query);
  }

  getSentinel2ById(id: string): Observable<any> {
    return this.getById('/satellite/Sentinel2ProductMetadata/GetById', id);
  }

  // Inspire Metadata
  getInspireMetadataById(id: string): Observable<any> {
    return this.getById('/satellite/SentinelInspireMetadata/GetById', id);
  }

  // CustomGet: расширенная фильтрация продуктов
  customGet(request: CustomGetRequest): Observable<SatelliteProductResponse> {
    return this.post('/common/SatelliteProduct/CustomGet', request);
  }

  // Update satellite product
  updateProduct(id: string, updateData: Partial<SatelliteProduct>): Observable<SatelliteProduct> {
    return this.put(`/common/SatelliteProduct/Update/${id}`, updateData);
  }

  // Update product isPublic status specifically
  updateProductPublicStatus(id: string, isPublic: boolean): Observable<any> {
    return this.put(`/common/SatelliteProduct/UpdatePublicStatus/${id}`, isPublic );
  }
}
